import BigNumber from 'bignumber.js';
const MINOR_UNITS = 2;
/*
 * DECIMAL_PLACES: maximum number of decimal places when using division
 * ROUNDING_MODE: use bankers' rounding
 */
export const CurrencyBN = BigNumber.clone({
    DECIMAL_PLACES: 2,
    ROUNDING_MODE: BigNumber.ROUND_HALF_EVEN,
});
const parse = (value) => typeof value === 'string' ? value.replace(/[^0-9.-]+/g, '') : value;
/**
 * @param value A major unit string
 * @returns Currency as a major unit number (e.g. dollars).
 */
export const parseToMajorUnit = (value) => new CurrencyBN(Number(parse(value))).toNumber();
/**
 * @param value A major unit string
 * @returns Currency as a minor unit number (e.g. cents)
 */
export const parseToMinorUnit = (value) => toMinorUnit(Number(parse(value)));
/**
 * @param value A major unit value (5.99)
 * @returns Currency in a minor unit amount (e.g. 5.99 -> 599, dollars to cents)
 */
export const toMinorUnit = (value) => new CurrencyBN(!value ? 0 : value)
    .decimalPlaces(MINOR_UNITS)
    .times(Math.pow(10, MINOR_UNITS))
    .toNumber();
/**
 * @param value A minor-unit value
 * @returns Currency in a major unit amount (e.g. 599 -> 5.99, cents to dollars)
 */
export const toMajorUnit = (value) => new CurrencyBN(!value ? 0 : value)
    .dividedBy(Math.pow(10, MINOR_UNITS))
    .toNumber();
/**
 * Formats a major unit amount to a currency string (e.g. 1.25 => "$1.25")
 *
 * @param majorAmount major unit amount (e.g. dollars)
 * @param options options object
 * @param options.bcpTag
 * @param options.currency
 * @param options.includeSymbol
 * @returns The currency in a localized presentation.
 */
export const formatMajorAmount = (majorAmount, { bcpTag = 'en-US', currency = 'USD', includeSymbol = true, } = {}) => {
    const formatter = new Intl.NumberFormat(bcpTag, {
        currency,
        style: 'currency',
    });
    return includeSymbol
        ? formatter.format(majorAmount)
        : formatter
            .formatToParts(majorAmount)
            .reduce((acc, { type, value }) => (type === 'currency' ? acc : acc + value), '');
};
/**
 * Formats a minor unit amount as a major unit currency string (e.g. 125 => "$1.25")
 *
 * @param minorAmount minor unit amount (e.g. cents)
 * @param options options object
 * @returns The currency in a localized presentation.
 */
export const formatMinorAmount = (minorAmount, options) => formatMajorAmount(toMajorUnit(minorAmount), options);
/**
 * Converts an amount from cents to dollars as compact as possible. If the amount is 700 it will be converted to $7. If the amount is 712 it will be converted to $7.12.
 *
 * @param minorAmount minor unit amount (e.g. cents)
 * @param options options object
 * @returns The currency in a localized presentation.
 */
export const formatAmountCompact = (minorAmount, options) => {
    const amount = formatMinorAmount(minorAmount, options);
    if (amount.endsWith('.00')) {
        return amount.slice(0, -3);
    }
    return amount;
};
export const formatPriceDifference = (amount, options) => {
    return `${amount > 0 ? '+' : '-'}${formatMinorAmount(Math.abs(amount), options)}`;
};
//# sourceMappingURL=index.js.map